import { useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import Navbar from '../components/Header10';
import Footer from '../components/Footer5';
import { apiFetch, friendlyMessage } from '../../api/apiClient';
import { LoadingState, ErrorState } from '../../shared/components/status';
import { GREEN, CHARCOAL, BORDER, MUTED, MUTED_FAINT, STONE50,
  labelMono, bodyFont } from '../../theme/tokens2';

const PAGE_SIZE = 50;

// ─── Columns ──────────────────────────────────────────────────────────────────
// Kept in one place so the header and the rows always match.
// GRID_COLS must have one size per column.
const COLUMNS = [
  { label: 'Account',            field: 'name',            sortable: false },
  { label: 'Followers',          field: 'insta_followers', sortable: true  },
  { label: 'Posts',              field: 'insta_posts',     sortable: true  },
  { label: 'Google Rating',      field: 'google_rating',   sortable: true  },
  { label: 'Predominant Colors', field: null,              sortable: false, tooltip: 'The main colors appearing most frequently across the account\'s most recent posts.' },
  { label: 'Also Seen In',       field: null,              sortable: false },
];

const GRID_COLS = '1fr 100px 80px 120px 1.5fr 1fr';

// ─── Sort icon ────────────────────────────────────────────────────────────────
function SortIcon({ field, sortField, sortDir }) {
  const active = sortField === field;
  const color  = active ? GREEN : 'rgba(0,0,0,0.20)';
  return sortDir === 'asc' && active
    ? <KeyboardArrowUpIcon   aria-hidden sx={{ fontSize: '0.75rem', color }} />
    : <KeyboardArrowDownIcon aria-hidden sx={{ fontSize: '0.75rem', color }} />;
}

// ─── Colour bar ───────────────────────────────────────────────────────────────
function PredominantColorsBar({ item }) {
  const colors = [
    item.k1_col, item.k2_col, item.k3_col, item.k4_col, item.k5_col,
    item.k6_col, item.k7_col, item.k8_col, item.k9_col, item.k10_col,
  ];
  const pcts = [
    item.k1_pct, item.k2_pct, item.k3_pct, item.k4_pct, item.k5_pct,
    item.k6_pct, item.k7_pct, item.k8_pct, item.k9_pct, item.k10_pct,
  ];
  const validSegments = colors
    .map((col, idx) => ({ color: col, pct: pcts[idx] ? Number(pcts[idx]) : 0 }))
    .filter((seg) => seg.color && seg.pct > 0);

  return (
    <Box
      role="img"
      aria-label="Predominant colors"
      sx={{ display: 'flex', width: '100%', height: 16, overflow: 'hidden', boxShadow: 'inset 0 0 2px rgba(0,0,0,0.15)' }}
    >
      {validSegments.map((seg, i) => (
        <Box key={i} sx={{ backgroundColor: seg.color, width: `${seg.pct}%`, height: '100%', transition: 'width 0.3s ease' }} />
      ))}
    </Box>
  );
}

// ─── Endorsement links ("Also seen in") ───────────────────────────────────────
function Endorsements({ items }) {
  return items
    .slice()
    .sort((a, b) => a.endorser.localeCompare(b.endorser))
    .map((e, idx) => (
      <Box key={e.endorser + idx} component="a" href={e.link}
        target="_blank" rel="noopener noreferrer"
        sx={{
          ...labelMono, fontSize: '9px', color: MUTED,
          border: `1px solid ${BORDER}`, px: '8px', py: '4px',
          textDecoration: 'none',
          '&:hover': { backgroundColor: STONE50, color: CHARCOAL },
        }}
      >
        {e.endorser}
      </Box>
    ));
}

const handleSx = {
  display: 'inline-block', ...labelMono, fontSize: '9px', color: GREEN,
  border: '1px solid rgba(33,131,51,0.30)', px: '8px', py: '4px',
  textDecoration: 'none',
  '&:hover': { backgroundColor: 'rgba(33,131,51,0.06)', borderColor: GREEN },
};

const instagramUrl = (acct) => `https://instagram.com/${(acct || '').replace(/^@/, '')}`;

const formatRating = (item) => (item.google_rating != null
  ? `${item.google_rating} (${item.google_ratingtotal?.toLocaleString() ?? '—'})`
  : '—');

// ─── Table row ────────────────────────────────────────────────────────────────
function AccountRow({ item, index }) {
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const panelId = `social-more-${index}`;

  return (
    <Box sx={{ borderBottom: `1px solid ${BORDER}`, '&:last-child': { borderBottom: 'none' } }}>

      {/* ── Desktop row ── */}
      <Box role="row" sx={{
        display: { xs: 'none', md: 'grid' },
        gridTemplateColumns: GRID_COLS,
        alignItems: 'center',
        backgroundColor: index % 2 === 0 ? '#fff' : STONE50,
      }}>
        <Box role="cell" sx={{ px: 4, py: 1, minWidth: 0, overflow: 'hidden' }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: CHARCOAL, ...bodyFont,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {item.name_insta}
          </Typography>
          <Typography component="a" href={instagramUrl(item.insta_acct)}
            target="_blank" rel="noopener noreferrer" sx={{ ...handleSx, mt: 0.5 }}>
            {item.insta_acct}
          </Typography>
        </Box>

        <Box role="cell" sx={{ px: 2, py: 3 }}>
          <Typography sx={{ fontSize: '0.875rem', color: MUTED, ...bodyFont }}>
            {item.insta_followers?.toLocaleString() ?? '—'}
          </Typography>
        </Box>

        <Box role="cell" sx={{ px: 2, py: 3 }}>
          <Typography sx={{ fontSize: '0.875rem', color: MUTED, ...bodyFont }}>
            {item.insta_posts?.toLocaleString() ?? '—'}
          </Typography>
        </Box>

        <Box role="cell" sx={{ px: 2, py: 3 }}>
          <Typography sx={{ fontSize: '0.875rem', color: MUTED, ...bodyFont }}>
            {formatRating(item)}
          </Typography>
        </Box>

        <Box role="cell" sx={{ px: 2, py: 3 }}>
          <PredominantColorsBar item={item} />
        </Box>

        <Box role="cell" sx={{ px: 2, py: 3, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {item.endorsements?.length > 0 ? (
            <Endorsements items={item.endorsements} />
          ) : (
            <Typography sx={{ fontSize: '0.8rem', color: MUTED_FAINT, ...bodyFont }}>—</Typography>
          )}
        </Box>
      </Box>

      {/* ── Mobile card ── */}
      <Box sx={{
        display: { xs: 'flex', md: 'none' },
        flexDirection: 'column',
        backgroundColor: index % 2 === 0 ? '#fff' : STONE50,
        px: 3, pt: 3, pb: mobileExpanded ? 0 : 3,
      }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 2, alignItems: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: CHARCOAL, ...bodyFont, mb: 0.75 }}>
              {item.name_insta}
            </Typography>
            <Typography component="a" href={instagramUrl(item.insta_acct)}
              target="_blank" rel="noopener noreferrer" sx={handleSx}>
              {item.insta_acct}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT, mb: 0.5 }}>
              IG FOLLOWERS
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: CHARCOAL, ...bodyFont, mb: 1 }}>
              {item.insta_followers?.toLocaleString() ?? '—'}
            </Typography>
            <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT, mb: 0.5 }}>
              GOOGLE RATING
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: CHARCOAL, ...bodyFont, mb: 1 }}>
              {formatRating(item)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 0.5 }}>
          <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT, mb: 1 }}>
            PREDOMINANT COLORS
          </Typography>
          <PredominantColorsBar item={item} />
        </Box>

        <Box
          component="button"
          type="button"
          aria-expanded={mobileExpanded}
          aria-controls={panelId}
          onClick={() => setMobileExpanded((e) => !e)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center',
            mt: 2, py: 1.5, width: '100%',
            background: 'none', border: 'none', borderTop: `1px solid ${BORDER}`,
            cursor: 'pointer', color: MUTED,
            ...labelMono, fontSize: '9px',
            transition: 'color 0.2s ease',
            '&:hover': { color: CHARCOAL },
          }}
        >
          {mobileExpanded ? 'Less' : 'More'}
          {mobileExpanded
            ? <KeyboardArrowUpIcon aria-hidden sx={{ fontSize: '0.75rem' }} />
            : <KeyboardArrowDownIcon aria-hidden sx={{ fontSize: '0.75rem' }} />}
        </Box>

        <Box id={panelId} sx={{
          overflow: 'hidden',
          maxHeight: mobileExpanded ? '300px' : '0px',
          transition: 'max-height 0.3s cubic-bezier(0.22,1,0.36,1)',
        }}>
          <Box sx={{ pt: 2, pb: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT, mb: 0.5 }}>
                # IG POSTS
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: MUTED, ...bodyFont }}>
                {item.insta_posts?.toLocaleString() ?? '—'}
              </Typography>
            </Box>

            {item.endorsements?.length > 0 && (
              <Box>
                <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT, mb: 1 }}>
                  ALSO SEEN IN
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  <Endorsements items={item.endorsements} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function SocialsPage() {
  const [socialsData, setSocialsData] = useState({ content: [], totalPages: 0 });
  const [currentPage, setCurrentPage] = useState(0);
  const [status,      setStatus]      = useState('loading'); // 'loading' | 'ready' | 'error'
  const [error,       setError]       = useState('');
  const [sortField,   setSortField]   = useState('insta_followers');
  const [sortDir,     setSortDir]     = useState('desc');

  // Loads one page. The sort is always sent, so changing page keeps the
  // chosen order (before, page 2 onwards lost it).
  const fetchPage = useCallback(async (page, field, dir) => {
    setStatus('loading');
    try {
      const params = new URLSearchParams({ page, size: PAGE_SIZE, sort: field, direction: dir });
      const data = await apiFetch(`/api/socials?${params}`, { auth: false });
      setSocialsData(data || { content: [], totalPages: 0 });
      setStatus('ready');
    } catch (e) {
      setError(friendlyMessage(e));
      setStatus('error');
    }
  }, []);

  useEffect(() => { fetchPage(currentPage, sortField, sortDir); }, [fetchPage, currentPage, sortField, sortDir]);

  // Clicking the active column flips the direction; a new column starts high-to-low.
  const handleSort = (field) => {
    if (!field) return;
    setSortDir(sortField === field ? (sortDir === 'asc' ? 'desc' : 'asc') : 'desc');
    setSortField(field);
    setCurrentPage(0);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const items = socialsData.content || [];
  const totalPages = socialsData.totalPages || 0;

  const paginationBtnSx = (disabled) => ({
    ...labelMono,
    color: disabled ? 'rgba(0,0,0,0.25)' : CHARCOAL,
    border: `1px solid ${BORDER}`,
    px: '16px', py: '8px',
    background: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
    '&:hover:not(:disabled)': { color: '#fff', backgroundColor: CHARCOAL, borderColor: CHARCOAL },
  });

  const ariaSort = (field) => (sortField === field ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none');

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', ...bodyFont }}>

      <Navbar />

      {/* ── Page header ── */}
      <Box component="main" sx={{ pt: '64px' }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 3, md: 6 } }}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 4, mb: 2 }}>
            <Typography sx={{ ...labelMono, color: GREEN }}>INSPIRATION</Typography>
            <Box sx={{ height: '1px', width: 40, backgroundColor: 'rgba(33,131,51,0.30)' }} />
          </Box>

          <Typography
            component="h1"
            sx={{
              fontSize: 'clamp(2.2rem, 5vw, 3.8rem)',
              fontWeight: 300, lineHeight: 1.06, letterSpacing: '-0.03em',
              color: CHARCOAL, mb: 2, ...bodyFont,
            }}
          >
            Design Accounts{' '}
            <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 300, color: GREEN }}>worth following</Box>
          </Typography>

          <Typography sx={{
            fontSize: '1rem', color: MUTED, lineHeight: 1.7, maxWidth: 640, mb: 4,
            ...bodyFont,
          }}>
            Singapore-based renovation Instagram accounts, ranked by reach. See their color palettes and where they've been featured.
          </Typography>
        </Box>

        {/* ── Content ── */}
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 3, md: 6 }, pb: 6 }}>

          {/* Mobile sort control */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT }}>
              SORT BY
            </Typography>
            {[
              { label: 'Followers', field: 'insta_followers' },
              { label: 'Posts',     field: 'insta_posts'     },
              { label: 'Rating',    field: 'google_rating'   },
            ].map((opt) => {
              const active = sortField === opt.field;
              return (
                <Box
                  key={opt.field}
                  component="button"
                  type="button"
                  aria-pressed={active}
                  onClick={() => handleSort(opt.field)}
                  sx={{
                    ...labelMono, fontSize: '9px',
                    color: active ? GREEN : MUTED,
                    border: `1px solid ${active ? 'rgba(33,131,51,0.40)' : BORDER}`,
                    background: active ? 'rgba(33,131,51,0.06)' : 'none',
                    px: '10px', py: '6px',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    transition: 'color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease',
                  }}
                >
                  {opt.label}
                  <SortIcon field={opt.field} sortField={sortField} sortDir={sortDir} />
                </Box>
              );
            })}
          </Box>

          {status === 'loading' && <LoadingState label="Loading accounts…" />}

          {status === 'error' && (
            <ErrorState
              title="We couldn't load the accounts"
              message={error}
              onRetry={() => fetchPage(currentPage, sortField, sortDir)}
            />
          )}

          {status === 'ready' && (
            <>
              <Typography sx={{ ...labelMono, color: MUTED_FAINT, fontSize: '9px', mb: 1 }}>
                {items.length} account{items.length !== 1 ? 's' : ''} on this page
                {totalPages > 1 && ` — page ${currentPage + 1} of ${totalPages}`}
                {' · Refreshed monthly'}
              </Typography>

              <Box role="table" aria-label="Design accounts" sx={{ border: `1px solid ${BORDER}`, overflow: 'hidden' }}>

                {/* Table header */}
                <Box role="row" sx={{
                  display: { xs: 'none', md: 'grid' },
                  gridTemplateColumns: GRID_COLS,
                  backgroundColor: STONE50,
                  borderBottom: `1px solid ${BORDER}`,
                }}>
                  {COLUMNS.map((col) => (
                    <Box
                      key={col.label}
                      role="columnheader"
                      aria-sort={col.sortable ? ariaSort(col.field) : undefined}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75,
                        px: col.label === 'Account' ? { xs: 3, md: 4 } : 2,
                        py: 2,
                      }}
                    >
                      {col.sortable ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => handleSort(col.field)}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 0.75,
                            ...labelMono, fontSize: '9px',
                            color: sortField === col.field ? CHARCOAL : MUTED,
                            background: 'none', border: 'none', p: 0,
                            cursor: 'pointer',
                            '&:hover': { color: CHARCOAL },
                          }}
                        >
                          {col.label}
                          <SortIcon field={col.field} sortField={sortField} sortDir={sortDir} />
                        </Box>
                      ) : (
                        <Typography component="span" sx={{ ...labelMono, fontSize: '9px', color: MUTED }}>
                          {col.label}
                        </Typography>
                      )}
                      {col.tooltip && (
                        <Tooltip title={col.tooltip} arrow>
                          <IconButton size="small" aria-label={col.tooltip} sx={{ p: 0, color: 'rgba(0,0,0,0.35)' }}>
                            <InfoOutlinedIcon sx={{ fontSize: '0.75rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                </Box>

                {/* Rows */}
                {items.length === 0 ? (
                  <Box sx={{ px: 4, py: 10, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.875rem', color: MUTED, ...bodyFont }}>
                      No accounts available.
                    </Typography>
                  </Box>
                ) : (
                  items.map((item, i) => (
                    <AccountRow key={item.insta_acct} item={item} index={i} />
                  ))
                )}
              </Box>

              <Typography sx={{ ...labelMono, color: MUTED_FAINT, fontSize: '9px', mt: 3 }}>
                Followers and posts sourced from public Instagram profiles. Ratings are from Google reviews.
                Keen on being featured? Reach out at hello@savvyrenovator.com.
              </Typography>

              {totalPages > 1 && (
                <Box component="nav" aria-label="Pages" sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: 2, mt: 6,
                }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 0}
                    sx={paginationBtnSx(currentPage === 0)}
                  >
                    ← Previous
                  </Box>

                  <Typography sx={{ ...labelMono, color: MUTED, fontSize: '9px' }}>
                    {currentPage + 1} / {totalPages}
                  </Typography>

                  <Box
                    component="button"
                    type="button"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage + 1 >= totalPages}
                    sx={paginationBtnSx(currentPage + 1 >= totalPages)}
                  >
                    Next →
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <Footer />

    </Box>
  );
}
